import React, { useCallback, useState } from 'react';
import { DatePicker } from 'antd-mobile';
import { findMessage, BCInput, BCInputRef, IInputChangeEventArgs } from '../';
import { IBCMobileTimePickerProps } from './entities';
import Styles from './assets/timePickerStyles.scss';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import updateLocale from 'dayjs/plugin/updateLocale';
import { isNullOrUndefined } from '@bimser/core';
import { localizeHourUnits, removeAmPmUnit } from './components/Use12Hours';
import Icon from '@bimser/icons/16/overtime';

dayjs.extend(localizedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(updateLocale);

export default function BCMobileTimePicker(props: IBCMobileTimePickerProps) {
	const [visible, setVisible] = useState(false);
	const [isPm, setIsPm] = useState(false);
	const inputRef = React.useRef<BCInputRef>();

	React.useEffect(() => {
		if (inputRef.current) {
			inputRef.current.input.readOnly = true;
		}
		if (props.use12Hours) {
			localizeHourUnits(props, setIsPm, isPm);
		}

		return () => {
			removeAmPmUnit();
		};
	}, [inputRef.current, props.use12Hours]);

	const get12HourTimeString = (
		time: dayjs.Dayjs,
		isPm: boolean,
		amLabel: string,
		pmLabel: string
	): string => {
		const hourValue = time.hour();
		const baseFormat = time.format('hh:mm:ss');

		if (isPm && hourValue < 12) {
			return time.add(12, 'hour').format('hh:mm:ss') + ' ' + pmLabel;
		} else if (!isPm && hourValue >= 12) {
			return time.subtract(12, 'hour').format('hh:mm:ss') + ' ' + amLabel;
		}
		return baseFormat + (isPm ? ' ' + pmLabel : ' ' + amLabel);
	};

	const getTimeString = (
		props: IBCMobileTimePickerProps,
		time: dayjs.Dayjs,
		isPm: boolean
	): string => {
		if (props.use12Hours) {
			const amLabel = findMessage.get('102777');
			const pmLabel = findMessage.get('102778');
			return get12HourTimeString(time, isPm, amLabel, pmLabel);
		}
		return time.format(props.format || 'HH:mm:ss');
	};

	function formatTime(value: dayjs.Dayjs): string {
		const time = dayjs(value);

		if (props.use12Hours) {
			const amLabel = findMessage.get('102777');
			const pmLabel = findMessage.get('102778');
			return `${time.format('hh:mm:ss')} ${isPm ? pmLabel : amLabel}`;
		}
		return time.format(props.format || 'HH:mm:ss');
	}

	function onConfirmValue(value: Date) {
		const time = dayjs(value);
		const timeString = getTimeString(props, time, isPm);

		if (props.onChange) {
			props.onChange(time, time.toISOString(), timeString);
		}
	}

	const labelRenderer = useCallback(
		(type: string, data: number) => {
			switch (type) {
				case 'year':
					return <div className={Styles.columns}></div>;
				case 'month':
					return <div className={Styles.columns}></div>;
				case 'day':
					return <div className={Styles.columns}></div>;
				case 'hour':
					return data;
				case 'minute':
					return data;
				case 'second':
					return data;
				default:
					return data;
			}
		},
		[props.use12Hours]
	);

	function getPrecision() {
		if (isNullOrUndefined(props.format)) {
			return 'minute';
		} else if (props.format.includes('ss')) {
			return 'second';
		} else if (props.format.includes('mm')) {
			return 'minute';
		} else if (props.format.includes('HH')) {
			return 'hour';
		}
		return 'day';
	}

	function onChangeVisible(pickerVisible: boolean) {
		setVisible(pickerVisible);
	}

	function filterTimeIntervals(
		type: string,
		val: number,
		selectedHour?: number,
		selectedMinute?: number
	) {
		const isStepValid = (step: number | undefined) => (step ? val % step === 0 : true);

		const isDisabled = (values: number[], currentValue: number) =>
			values.includes(currentValue);

		switch (type) {
			case 'minute':
				if (
					selectedHour !== undefined &&
					props.disabledMinutes &&
					isDisabled(props.disabledMinutes(selectedHour), val)
				) {
					return false;
				}
				if (!isStepValid(props.minuteStep)) {
					return false;
				}
				break;
			case 'hour':
				if (props.use12Hours && (val < 1 || val > 12)) {
					return false;
				}
				const disabledHours = props.disabledHours ? props.disabledHours() : [];
				if (isDisabled(disabledHours, val)) {
					return false;
				}
				if (!isStepValid(props.hourStep)) {
					return false;
				}
				break;
			case 'second':
				if (
					selectedHour !== undefined &&
					selectedMinute !== undefined &&
					props.disabledSeconds &&
					isDisabled(props.disabledSeconds(selectedHour, selectedMinute), val)
				) {
					return false;
				}
				if (!isStepValid(props.secondStep)) {
					return false;
				}
				break;
			default:
				return true;
		}

		return true;
	}

	function timeFilter() {
		return {
			minute: (val: number) => filterTimeIntervals('minute', val),
			second: (val: number) => filterTimeIntervals('second', val),
			hour: (val: number) => filterTimeIntervals('hour', val)
		};
	}

	function handleChange(args: IInputChangeEventArgs) {
		const date = dayjs(args.data);
		if (date.isValid()) {
			const shortISODate = date.format().split('T')[0];
			const dateTimeOffsetString = date.format();
			props?.onChange(date, shortISODate, dateTimeOffsetString);
		} else {
			props?.onChange(null, null, null);
		}
	}

	return (
		<>
			<BCInput
				ref={inputRef}
				autoFocus={props.autoFocus}
				onClick={() => !props.disabled && !props.readOnly && onChangeVisible(true)}
				value={props.value ? formatTime(props.value) : null}
				disabled={props.disabled || props.readOnly}
				placeHolder={props.placeholder}
				size={props.size}
				allowClear={props.allowClear}
				style={props.style}
				textAlign={props.textAlign}
				suffix={!props.value ? <Icon /> : ''}
				onChange={handleChange}
			/>
			<DatePicker
				tabIndex={props.tabIndex}
				className={Styles.timePicker}
				filter={timeFilter()}
				visible={visible}
				onClose={() => {
					onChangeVisible(false);
					removeAmPmUnit();
				}}
				precision={getPrecision()}
				onConfirm={onConfirmValue}
				renderLabel={labelRenderer}
				confirmText={findMessage.get('100004')}
				cancelText={findMessage.get('100034')}
			/>
		</>
	);
}
